import React, { useEffect, useState, useRef } from 'react';
import cn from 'classnames';
import Image from 'next/image';

import { useKeenSlider } from 'keen-slider/react';
import 'keen-slider/keen-slider.min.css';

const Quote = ({ quotes, image, gradientText, centered = false }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [slideCount, setSlideCount] = useState(0);
  const [loaded, setLoaded] = useState(false);
  const [pause, setPause] = useState(false);
  const timer = useRef();

  const [sliderRef, slider] = useKeenSlider({
    loop: true,
    created(s) {
      setLoaded(true);
      setSlideCount(s.slides.length);
      s.container.addEventListener('mouseover', () => {
        setPause(true);
      });
      s.container.addEventListener('mouseout', () => {
        setPause(false);
      });
    },
    slideChanged(s) {
      setCurrentSlide(s.track.details.rel);
    },
    dragStarted: () => {
      setPause(true);
    },
    dragEnded: () => {
      setPause(false);
    }
  });

  useEffect(() => {
    if (slideCount > 1) {
      timer.current = setInterval(() => {
        if (!pause && slider) {
          slider.current?.next();
        }
      }, 5000);
      return () => {
        clearInterval(timer.current);
      };
    }
  }, [pause, slider]);

  return (
    <div className="z-10 space-y-10 sm:space-y-16">
      <div className="relative overflow-hidden bg-gray-900 rounded-xl">
        <div ref={sliderRef} className="keen-slider">
          {quotes.map(quote => (
            <div className="flex flex-col lg:gap-8 keen-slider__slide lg:flex-row lg:flex-nowrap lg:items-center">
              <BlockQuote
                quote={quote}
                image={image}
                gradientText={gradientText}
                centered={centered}
              />
            </div>
          ))}
        </div>
        {!image && <div className="h-[4px] w-full bg-gradient-x absolute bottom-0" />}
      </div>
      {loaded && slideCount > 1 && (
        <div className="sm:space-x-[72px] justify-center items-center mx-auto sm:max-w-max flex">
          <div
            className={cn(
              'items-center justify-center hidden w-10 h-10 rotate-180 rounded-full cursor-pointer select-none sm:flex'
            )}
            onClick={e => e.stopPropagation() || slider.current?.prev()}
          >
            <span className="text-xl">{`\u2192`}</span>
          </div>
          {slider && (
            <div className="flex justify-center space-x-2 transform bottom-4 max-w-max">
              {[...Array(slider.current.slides.length).keys()].map(idx => (
                <span
                  key={idx}
                  onClick={() => slider.current?.moveToIdx(idx)}
                  className={cn(
                    'cursor-pointer rounded-full w-[10px] h-[10px]',
                    currentSlide === idx ? 'bg-white' : 'bg-[#37383B]'
                  )}
                />
              ))}
            </div>
          )}
          <div
            className={cn(
              'items-center justify-center hidden w-10 h-10 rounded-full cursor-pointer select-none sm:flex'
            )}
            onClick={e => e.stopPropagation() || slider.current?.next()}
          >
            <span className="text-xl">{`\u2192`}</span>
          </div>
        </div>
      )}
    </div>
  );
};

const BlockQuote = ({ quote, image, gradientText, centered = false }) => (
  <>
    {!image && (
      <div
        className={cn(
          'relative flex w-4/12 h-16 m-8 mb-0 lg:items-center lg:h-full lg:m-0 md:px-10',
          centered && 'justify-center lg:justify-start'
        )}
      >
        <div className="relative w-full h-full max-w-xs max-h-16">
          {quote.logo && <Image quality={100} src={quote.logo.src} layout="fill" />}
        </div>
      </div>
    )}
    {image && (
      <div className="relative h-52 lg:h-full lg:w-6/12">
        <Image layout="fill" quality={100} src={quote.img.src} objectFit="cover" />
      </div>
    )}
    <div
      className={cn(
        'w-full p-8 lg:p-20 lg:pl-0',
        centered && 'text-center lg:text-left',
        image ? 'lg:w-6/12 lg:pl-8' : 'lg:w-8/12'
      )}
    >
      {image && (
        <div
          className={cn(
            'relative flex mb-8 max-w-4/12 lg:h-8',
            centered && 'justify-center lg:justify-start'
          )}
        >
          <Image
            quality={100}
            src={quote.logo.src}
            width={quote.logo.width}
            height={quote.logo.height}
            layout="fixed"
          />
        </div>
      )}
      <blockquote
        className={cn('text-base sm:text-lg lg:leading-relaxed mb-8', {
          'lg:text-2xl': !image
        })}
      >
        {quote.content}
      </blockquote>
      <h4
        className={cn(
          'mb-4 text-lg leading-snug sm:mb-0 inline-block',
          { 'lg:text-2xl': !image },
          gradientText ? 'text-transform-gradient font-aeonik sm:text-xl' : 'font-bold'
        )}
      >
        {quote.profile}
      </h4>
      <p
        className={cn('text-base sm:text-lg mt-2 font-aeonik text-gray-300', {
          'lg:text-xl': !image
        })}
      >
        {quote.position}
      </p>
    </div>
  </>
);

Quote.defaultProps = {
  quotes: [
    {
      logo: {
        src: '/static/images/pages/homepage/quotes/nuro.svg',
        alt: 'Nuro'
      },
      content:
        "“We really value our partnership with Scale and their ability to deliver custom workflows that suit our needs across a variety of mapping and deep learning models. Scale's high-quality, rapidly labeled data will enable us to ramp our map expansion, allowing us to provide more service to customers like Dominos.”",
      profile: 'Eleonor Concepcion',
      position: 'Software Operations Program Manager, Nuro'
    }
  ],
  image: false,
  gradientText: true
};

export default Quote;
